/**
 * Pendo Analytics — safe wrapper helpers.
 *
 * All calls to the Pendo agent are guarded by a `isPendoReady()` check so
 * that this module is safe to import and call even before the async Pendo
 * script has finished loading (or when Pendo has been disabled for a given
 * environment).
 *
 * IMPORTANT: Never call `window.pendo` directly outside this module —
 * always go through these helpers so there is a single place to gate
 * environment behaviour.
 */

/** Returns true when the Pendo agent has been fully loaded by the browser. */
export const isPendoReady = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.pendo !== "undefined" &&
  typeof window.pendo.initialize === "function";

/**
 * Initialise Pendo with a visitor and account identity.
 *
 * Call this once immediately after the user has been authenticated.
 * Subsequent calls on the same page session (e.g. from `useEffect` re-runs)
 * are safe — Pendo ignores duplicate `initialize` calls with the same IDs.
 *
 * @param visitorId   - Unique, stable user identifier (user.id from auth session)
 * @param accountId   - Unique account / tenant identifier (tenantId)
 * @param visitorMeta - Optional extra visitor fields (email, full_name, role)
 */
export const pendoInitialize = (
  visitorId: string,
  accountId: string,
  visitorMeta: {
    email?: string;
    full_name?: string;
    role?: string;
  } = {}
): void => {
  if (!isPendoReady()) return;
  window.pendo.initialize({
    visitor: {
      id: visitorId,
      ...visitorMeta,
    },
    account: {
      id: accountId,
    },
  });
};

/**
 * Re-identify the current visitor and account.
 *
 * Use when the logged-in user changes within the same SPA session (e.g. after
 * a tenant switch or impersonation change).
 */
export const pendoIdentify = (
  visitorId: string,
  accountId: string,
  visitorMeta: {
    email?: string;
    full_name?: string;
    role?: string;
  } = {}
): void => {
  if (!isPendoReady()) return;
  window.pendo.identify({
    visitor: {
      id: visitorId,
      ...visitorMeta,
    },
    account: {
      id: accountId,
    },
  });
};

/**
 * Fire a synthetic page-load event for SPA route changes.
 *
 * Call this inside a `useEffect` that depends on the current route pathname
 * so Pendo records each virtual page view as the user navigates.
 */
export const pendoPageLoad = (): void => {
  if (!isPendoReady()) return;
  window.pendo.pageLoad();
};